import type { ReactNode } from "react";

/** Renders plain text with any http(s) URL in it turned into a clickable
 * link — used for the Staff-authored "Notes & Links" free text on a guide
 * entry (see StaffGuideNote / GuideNotesEditor), which is exactly where
 * someone would paste a reference URL with no markup around it. Not used
 * on Admin-authored guide/flow content (spiel_text, internal_note) — those
 * stay plain text, out of scope of this. */

const URL_PATTERN = /https?:\/\/[^\s]+/g;
// Trailing characters almost never meant to be part of the URL itself —
// sentence-ending punctuation, or a closing bracket that opened *before*
// the URL started (e.g. "(see https://example.com)"). Stripped off the
// match and rendered as plain text after the link instead. Not bracket-
// balance-aware, so a URL that legitimately ends in ')' (some Wikipedia
// links do) still gets trimmed — a known, accepted trade-off for a
// lightweight notes field, not a public rich-text renderer.
const TRAILING_PUNCTUATION = /[)\].,;:!?'"]+$/;

export function Linkify({ text, className }: { text: string; className?: string }) {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;

  // matchAll (unlike repeated .exec()/.test() on the same regex object)
  // doesn't carry `lastIndex` state across calls, so this can't fall into
  // the classic global-regex re-entrancy bug.
  for (const match of text.matchAll(URL_PATTERN)) {
    const start = match.index;
    let url = match[0];
    const trailing = url.match(TRAILING_PUNCTUATION)?.[0] ?? "";
    if (trailing) url = url.slice(0, -trailing.length);
    if (!url) continue; // the "URL" was punctuation start-to-end — nothing to link

    if (start > lastIndex) nodes.push(<span key={key++}>{text.slice(lastIndex, start)}</span>);
    nodes.push(
      <a
        key={key++}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="break-all text-primary underline-offset-2 hover:underline"
      >
        {url}
      </a>
    );
    if (trailing) nodes.push(<span key={key++}>{trailing}</span>);
    lastIndex = start + match[0].length;
  }
  if (lastIndex < text.length) nodes.push(<span key={key++}>{text.slice(lastIndex)}</span>);

  return <p className={className}>{nodes}</p>;
}
